import styled from "styled-components";
import { useTranslation } from "react-i18next";
import Coupon from "./coupon";
import { CouponStatus, CouponType } from "../constant/index";
import { useGlobalStore } from "@/store/GlobalStore";
import { useCurrentCouponList } from "@/pages/desktop-coupon/hooks/useCurrentCouponList";
import { isOverTimeCoupon } from "@/pages/desktop-coupon/helper/isOverTimeCoupon";
import { useTabStore } from "../store/tab-store";
import { useGetTabsList } from "../hooks/useTableList";
import { useState } from "react";
import { RightOutlined } from "@ant-design/icons";
import { getCurrentCouponNum } from "../constant/coupon-filter";
import { Drawer } from "@renderbus/ui";
import { CouponRulesList } from "../constant/coupon-list";
import { ICouponDetailInfo } from "../types";
import Loading from "@/components/Loading";

interface StatusTagProps {
  isActive?: boolean;
  bgColor?: string;
}

const CouponTabDetailWrapper = styled.div`
  height: calc(100vh - 180px);
  overflow: hidden auto;
  padding: 20px 54px;
`;

const CouponTabAmount = styled.div<StatusTagProps>`
  font-size: 14px;
  line-height: 20px;
  font-weight: 600;
  color: #edf2f5;
  background: ${(props) => props.bgColor};
  border-radius: 4px;
  padding: 7px 12px;
  flex: 1;
`;
const CouponAllStatus = styled.div`
  background-color: #1e1f22;
  border-radius: 4px;
  margin-right: 20px;
`;
const CouponStatusTag = styled.div<StatusTagProps>`
  margin: 4px;
  background: ${(props) => (props.isActive ? "#3A3C41" : "transparent")};
  border-color: ${(props) => (props.isActive ? "#3A3C41" : "transparent")};
  font-weight: ${(props) => (props.isActive ? 600 : 400)};
  line-height: 20px;
  padding: 4px 10px 4px 4px;
  border-radius: 2px;
  color: ${(props) => (props.isActive ? "var(--main-color)" : "#A8B1B4")};
  cursor: pointer;
  display: inline-block;
  span {
    &::before {
      content: "";
      display: inline-block;
      width: 2px;
      height: 8px;
      background: #5b5d66;
      margin-left: 4px;
      margin-right: 6px;
    }
  }
`;
const CouponHeader = styled.div`
  display: flex;
`;

const CouponTabScopeText = styled.span`
  color: #b8c5cc;
  padding-right: 10px;
  font-weight: 400;
`;

const CouponUseRules = styled.div`
  color: var(--color-main-help);
  float: right;
  cursor: pointer;
`;

const CouponModalRules = styled.div`
  padding: 30px 16px 0 24px;
`;

const CouponRulesHeader = styled.div`
  color: #e0e1e1;
  font-weight: 600;
  line-height: 20px;
  padding-bottom: 10px;
`;
const CouponRulesContent = styled.div`
  color: #e0e1e1;
  font-weight: 400;
  font-size: 12px;
  line-height: 17px;
  padding-bottom: 4px;
`;
const CouponContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
`;
const LoadingContainer = styled.div`
  height: calc(100vh - 150px);
`;
export interface CouponTabProps {
  currentTab: CouponType;
}

export default function CouponTabDetail({ currentTab }: CouponTabProps) {
  const { t } = useTranslation("coupon");
  const { t: desktopT } = useTranslation("desktop");
  const { currencySymbol } = useGlobalStore();
  const { couponInfo, setCurrentStatus, currentStatus, loading, setShowRules } =
    useTabStore();
  const {
    currentCouponList,
    setCurrentCouponInfoList,
    tabRef,
    handleFilterCoupon,
  } = useCurrentCouponList();
  const { couponStatusList } = useGetTabsList();
  const [rulesOpenDrawer, setRulesOpenDrawer] = useState(false);
  const handleScroll = () => {
    if (!tabRef) return;
    if (!tabRef.current) return;

    const { clientHeight, scrollTop, scrollHeight } = tabRef.current;
    if (scrollHeight - clientHeight - scrollTop === 0) {
      if (currentStatus === CouponStatus.All)
        setCurrentCouponInfoList(
          couponInfo?.filter((item) => item.type === currentTab) || []
        );
      else handleFilterCoupon(currentTab, currentStatus, true);
    }
  };
  const count = couponInfo?.filter((p) => p.type === currentTab);
  return loading ? (
    <LoadingContainer>
      <Loading />
    </LoadingContainer>
  ) : (
    <CouponTabDetailWrapper onScroll={handleScroll} ref={tabRef}>
      <CouponHeader>
        <CouponAllStatus>
          {couponStatusList.map((item) => {
            return (
              <CouponStatusTag
                onClick={() => {
                  setCurrentStatus(item.value);
                  handleFilterCoupon(currentTab, item.value);
                }}
                key={item.value}
                isActive={item.value === currentStatus}
              >
                {item.label}
                <span>{getCurrentCouponNum(currentTab, item.value)}</span>
              </CouponStatusTag>
            );
          })}
        </CouponAllStatus>
        <CouponTabAmount bgColor={"var(--color-main-alpha-1)"}>
          <CouponTabScopeText>
            {`${desktopT("tablist_desktop_coupon_count")}`}
          </CouponTabScopeText>
          {`${currencySymbol}${count?.length ? count[0].amount : 0}`}
          <CouponUseRules onClick={() => setShowRules((pre) => !pre)}>
            {t("coupon_pages_use_rules")}
            <RightOutlined />
          </CouponUseRules>
        </CouponTabAmount>
      </CouponHeader>
      {currentCouponList.map((item) => {
        return (
          <CouponContainer
            key={item.name}
            style={{ width: "calc(100% + 22px)" }}
          >
            {item.data.map((p: ICouponDetailInfo) => {
              return (
                <Coupon
                  isOverTimeCoupon={isOverTimeCoupon}
                  data={p}
                  key={p.couponNo}
                />
              );
            })}
          </CouponContainer>
        );
      })}
      <Drawer
        width={600}
        open={rulesOpenDrawer}
        title={desktopT("desktop_rules_title")}
        onClose={() => setRulesOpenDrawer(false)}
        drawerContent={
          <>
            {CouponRulesList.map(
              (item: { rules: string[]; title: string; key: number }) => {
                return (
                  <CouponModalRules key={item.title}>
                    <CouponRulesHeader>{`${desktopT(
                      item.title
                    )}`}</CouponRulesHeader>
                    {item.rules.map((p, index) => {
                      return (
                        <CouponRulesContent key={p}>
                          {desktopT(p)}
                        </CouponRulesContent>
                      );
                    })}
                  </CouponModalRules>
                );
              }
            )}
          </>
        }
      />
    </CouponTabDetailWrapper>
  );
}
